import React, { useState, useEffect } from 'react';
import { Save, AlertCircle, Loader } from 'lucide-react';
import { Button, Card, Badge, LoadingSpinner } from '../UIComponents';
import { PermissionMatrix } from './PermissionMatrix';
import { customerAccessApi, sectionPermissionsApi } from '../../lib/permissions';
import { customersApi } from '../../lib/supabase';
import type { PortalUserFull, Customer } from '../../types';

interface CustomerAccessFormProps {
  user: PortalUserFull;
  onSave: () => void;
  onCancel: () => void;
}

export const CustomerAccessForm: React.FC<CustomerAccessFormProps> = ({
  user,
  onSave,
  onCancel,
}) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [selectedAccessId, setSelectedAccessId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [accessUpdates, setAccessUpdates] = useState<
    Record<
      string,
      { can_view: boolean; can_edit: boolean; can_delete: boolean }
    >
  >({});

  useEffect(() => {
    loadData();
  }, [user.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const customersData = await customersApi.getAll();
      setCustomers(customersData);

      // Initialize current access
      const updates: typeof accessUpdates = {};
      for (const access of user.customer_access) {
        updates[access.id] = {
          can_view: access.can_view,
          can_edit: access.can_edit,
          can_delete: access.can_delete,
        };
      }
      setAccessUpdates(updates);

      // Select first access if available
      if (user.customer_access.length > 0) {
        setSelectedAccessId(user.customer_access[0].id);
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const selectedAccess = user.customer_access.find(
    (a) => a.id === selectedAccessId
  );

  const handleGrantAccess = async (customerId: string) => {
    try {
      setSaving(true);
      setError(null);
      const access = await customerAccessApi.grantAccess(
        user.id,
        customerId,
        { canView: true, canEdit: false, canDelete: false }
      );
      await sectionPermissionsApi.createDefaultPermissions(access.id, false);
      setSelectedAccessId(access.id);
      await loadData();
    } catch (err) {
      console.error('Error granting access:', err);
      setError(err instanceof Error ? err.message : 'Failed to grant access');
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeAccess = async (accessId: string) => {
    try {
      setSaving(true);
      setError(null);
      const access = user.customer_access.find((a) => a.id === accessId);
      if (access) {
        await customerAccessApi.revokeAccess(user.id, access.customer_id);
        setSelectedAccessId(null);
        await loadData();
      }
    } catch (err) {
      console.error('Error revoking access:', err);
      setError(err instanceof Error ? err.message : 'Failed to revoke access');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePermissions = async () => {
    try {
      setSaving(true);
      setError(null);

      for (const [accessId, updates] of Object.entries(accessUpdates)) {
        await customerAccessApi.update(accessId, updates);
      }

      onSave();
    } catch (err) {
      console.error('Error saving permissions:', err);
      setError(err instanceof Error ? err.message : 'Failed to save permissions');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const unassignedCustomers = customers.filter(
    (c) =>
      !user.customer_access.some((a) => a.customer_id === c.id) &&
      user.highest_role_level > 1 // Not superuser
  );

  const isSuperuser = user.highest_role_level === 1;

  return (
    <div className="space-y-6">
      {error && (
        <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-red-900 text-sm">Error</p>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      {isSuperuser && (
        <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
          <p className="text-sm text-purple-700">
            This user is a superuser and has access to all customers.
          </p>
        </div>
      )}

      {!isSuperuser && (
        <>
          <div>
            <h3 className="font-semibold text-slate-900 mb-4">
              Assigned Customers
            </h3>
            {user.customer_access.length === 0 ? (
              <p className="text-sm text-slate-500 mb-4">
                No customers assigned yet.
              </p>
            ) : (
              <div className="space-y-2 mb-6">
                {user.customer_access.map((access) => (
                  <button
                    key={access.id}
                    onClick={() => setSelectedAccessId(access.id)}
                    className={`w-full p-4 rounded-lg border-2 text-left transition-all ${
                      selectedAccessId === access.id
                        ? 'border-accent bg-accent/5'
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-slate-900">
                          {access.customer_name}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          {access.can_view && (
                            <Badge color="blue">
                              View
                            </Badge>
                          )}
                          {access.can_edit && (
                            <Badge color="green">
                              Edit
                            </Badge>
                          )}
                          {access.can_delete && (
                            <Badge color="red">
                              Delete
                            </Badge>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRevokeAccess(access.id);
                        }}
                        className="text-xs text-red-600 hover:text-red-700 font-medium"
                        disabled={saving}
                      >
                        Revoke
                      </button>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {selectedAccess && (
            <Card className="bg-slate-50 border border-slate-200">
              <h3 className="font-semibold text-slate-900 mb-4">
                Section Permissions for {selectedAccess.customer_name}
              </h3>
              <PermissionMatrix access={selectedAccess} readOnly={false} />
            </Card>
          )}

          {unassignedCustomers.length > 0 && (
            <Card className="border border-amber-200 bg-amber-50">
              <h3 className="font-semibold text-slate-900 mb-4">
                Available Customers
              </h3>
              <div className="space-y-2">
                {unassignedCustomers.map((customer) => (
                  <div
                    key={customer.id}
                    className="flex items-center justify-between p-3 bg-white rounded-lg border border-slate-200"
                  >
                    <p className="text-sm text-slate-700">{customer.name}</p>
                    <Button
                      size="sm"
                      onClick={() => handleGrantAccess(customer.id)}
                      disabled={saving}
                    >
                      {saving ? (
                        <Loader size={14} className="animate-spin" />
                      ) : (
                        'Grant Access'
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}

      <div className="flex justify-end gap-3 pt-6 border-t border-slate-200">
        <Button variant="secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        {!isSuperuser && user.customer_access.length > 0 && (
          <Button onClick={handleSavePermissions} disabled={saving}>
            {saving ? (
              <>
                <Loader size={16} className="animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                Save Permissions
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
};
